import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EventResponse, EventClassResponse, EventServiceResponse, EventSponsorResponse, EventContactResponse} from '../../@shared/models/response.module';
import {EventsService} from '../../@shared/services/events-service';
import {AQH_EVE_0, AQH_EVE_6, AQH_EVE_11, _AQH_EVE_21, _AQH_EVE_24} from '../../@shared/models/input.module';
import {formatDateDMYDots} from '../../@core/utils/utils.service';

@Component({
  selector: 'app-event-details',
  standalone: false,
  templateUrl: './event-details.html',
  styleUrl: './event-details.css'
})
export class EventDetails implements OnInit {
  eventId: number;
  event: EventResponse | null = null;
  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  eventSponsors: EventSponsorResponse[] = [];
  eventContacts: EventContactResponse[] = [];

  loading: boolean = false;
  loadingClasses: boolean = false;
  loadingServices: boolean = false;
  loadingSponsors: boolean = false;
  loadingContacts: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService
  ) {}

  ngOnInit() {
    // Get event ID from route params
    this.route.params.subscribe(params => {
      this.eventId = +params['id'];
      if (this.eventId) {
        this.loadEventDetails();
        this.loadEventClasses();
        this.loadEventServices();
        this.loadEventSponsors();
        this.loadEventContacts();
      }
    });
  }

  loadEventDetails() {
    this.loading = true;
    const params: AQH_EVE_0 = {
      event_id: this.eventId
    };

    this.eventsService.getEvents(params).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.event = response.data[0];
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadEventClasses() {
    this.loadingClasses = true;
    const params: AQH_EVE_6 = {
      event_id: this.eventId
    };

    this.eventsService.getEventClasses(params).subscribe({
      next: (classes) => {
        this.eventClasses = classes || [];
        this.loadingClasses = false;
      },
      error: () => {
        this.loadingClasses = false;
      }
    });
  }

  loadEventServices() {
    this.loadingServices = true;
    const params: AQH_EVE_11 = {
      event_id: this.eventId
    };

    this.eventsService.getEventServices(params).subscribe({
      next: (services) => {
        this.eventServices = services || [];
        this.loadingServices = false;
      },
      error: () => {
        this.loadingServices = false;
      }
    });
  }

  loadEventSponsors() {
    this.loadingSponsors = true;
    const params: _AQH_EVE_21 = {
      event_id: this.eventId
    };

    this.eventsService.getEventSponsors(params).subscribe({
      next: (sponsors) => {
        this.eventSponsors = sponsors || [];
        this.loadingSponsors = false;
      },
      error: () => {
        this.loadingSponsors = false;
      }
    });
  }

  loadEventContacts() {
    this.loadingContacts = true;
    const params: _AQH_EVE_24 = {
      event_id: this.eventId
    };

    this.eventsService.getEventContacts(params).subscribe({
      next: (contacts) => {
        // Sort contacts by role_id with 0 first
        this.eventContacts = (contacts || []).sort((a, b) => {
          if (a.role_id === 0) return -1;
          if (b.role_id === 0) return 1;
          return a.role_id - b.role_id;
        });
        this.loadingContacts = false;
      },
      error: () => {
        this.loadingContacts = false;
      }
    });
  }

  getGoogleMapsUrl(event: EventResponse): string {
    const addressParts = [
      event.address,
      event.postal_code,
      event.city,
      event.country
    ].filter(part => part);

    const fullAddress = addressParts.join(', ');
    const encodedAddress = encodeURIComponent(fullAddress);

    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  registerToEvent() {
    console.log('Navigating to event registration:', this.eventId);
    this.router.navigate(['/event-registration', this.eventId]);
  }

  formatDateTime(dateTime: Date | string | null | undefined): string {
    if (!dateTime) return '';

    const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  getServiceAvailableSlots(service: EventServiceResponse): number {
    if (!service.max_num_registrations) return 0;
    const registeredCount = service.event_service_registrations?.length || 0;
    return Math.max(0, service.max_num_registrations - registeredCount);
  }

  getClassRegistrationCount(eventClass: EventClassResponse): number {
    return eventClass.event_class_registrations?.length || 0;
  }

  shouldAnimateSponsors(): boolean {
    return this.eventSponsors.length > 3;
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
